<script setup lang="ts">
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch
} from 'vue'
import state from './state'
import { initAuth, getToken, clearTokens, onSessionExpired } from './state/auth'
import { ORG_ID, AUTH_HOST, getLogoutUrl } from './lib/utils'
import { renderEmbedCode } from './lib/embeds'
import AdminConfirmModal from './components/admin/AdminConfirmModal.vue'
const AdminTools = defineAsyncComponent(
  () => import('./components/AdminTools.vue')
)
const StripeList = defineAsyncComponent(
  () => import('./components/StripeList.vue')
)

export interface Props {
  slug?: string
}

const props = withDefaults(defineProps<Props>(), {
  slug: ''
})

const loaded = ref(false)
const authenticated = ref(false)
const sessionExpiredModalOpen = ref(false)

const isVersionHistoryOpen = computed(() => state.versionHistoryOpen.value)

const isPreviewing = computed(() => {
  return !!state.pagePreviewHTML.value || isVersionHistoryOpen.value
})

const isReadonlyPage = computed(() => state.isReadonlyPage.value)

provide('isReadonlyPage', isReadonlyPage)

async function login() {
  const url = new URL(window.location.href)
  url.searchParams.delete('autologin')
  const path = encodeURIComponent(url.pathname + url.search)
  window.location.href = `${AUTH_HOST}/sso/authorize?org_id=${ORG_ID}&path=${path}`
}

function reauthenticate() {
  sessionExpiredModalOpen.value = false
  // This navigation is intentional and already-preserved via
  // persistUnsavedChanges(), so skip the native "leave site?" prompt.
  state.bypassUnloadWarning.value = true
  login()
}

function logout() {
  authenticated.value = false
  clearTokens()
  window.location.href = getLogoutUrl(
    AUTH_HOST,
    window.location.href,
    !!state.organization.value.config?.has_sso_pages
  )
}

function onLogout() {
  logout()
}

function getCurrentSlug() {
  return window.location.pathname?.split('.')?.[0] || '/'
}

async function onAuthenticated() {
  try {
    // @todo This could trigger a loader while we retrieve current page data.
    await state.initFromSlug(props.slug || getCurrentSlug())
    state.restoreUnsavedChanges()
    checkForPasswordPrompt()
    toggleRenderedStripes(true)
    loaded.value = true
  } catch (e) {
    console.warn('Failed to load page data: ', e)
  }
}

function onWindowPointerDown() {
  state.activeTrayId.value = '-1'
}

function onWindowClick(e: Event) {
  const target: HTMLElement | null = e?.target as HTMLElement | null
  const clickEl =
    target && target.getAttribute('data-vssl-action')
      ? (target as HTMLElement)
      : (target?.closest('[data-vssl-action]') as HTMLElement)

  if (clickEl && clickEl.getAttribute('data-vssl-action')) {
    e.preventDefault()
    e.stopPropagation()

    switch (clickEl.getAttribute('data-vssl-action')) {
      case 'logout':
        logout()
        break
      case 'login':
        login()
        break
    }
  }
}

function onWindowKeydown(e: KeyboardEvent) {
  if (!authenticated.value && e.ctrlKey && ['l', 'L'].includes(e.key)) {
    login()
  }
}

// If we landed on a 401 page, that means the site is password protected, and we need to refresh the page so that we get the authenticated view -BM
function checkForPasswordPrompt() {
  const passwordInput = document.getElementById('password')
  if (passwordInput) {
    window.location.reload()
  }
}

function toggleRenderedStripes(hide: boolean) {
  const page = document.querySelector(
    '.vssl-page:not(.vssl-page--editor)'
  ) as HTMLElement

  // Only hide page if the current slug corresponds with a managed page.
  if (page && state.isManagedPage.value) {
    page.style.display = hide ? 'none' : 'initial'
  }
}

function getBufferEls() {
  return [
    document.getElementById('vssl-navigation-buffer'),
    document.getElementById('vssl-toolbar-buffer')
  ]
}

watch(authenticated, async (val) => {
  if (val) {
    onAuthenticated()
  }
})

// Version history insets the whole rendered page (the theme's own header and
// footer included, which live outside [vssl-document]) on a grue background.
// The theme's page wrapper is whichever direct child of <body> contains the
// document, so it's found at runtime rather than assumed.
function toggleVersionHistoryInset(open: boolean) {
  document.body.classList.toggle('vssl-version-history-open', open)

  let pageRoot = document.querySelector('[vssl-document]')
  while (pageRoot && pageRoot.parentElement !== document.body) {
    pageRoot = pageRoot.parentElement
  }
  pageRoot?.classList.toggle('vssl-version-history-inset', open)
}

watch(isVersionHistoryOpen, toggleVersionHistoryInset)

watch(state.pagePreviewHTML, async (val) => {
  if (val) {
    await nextTick()
    const embedContainers = document.querySelectorAll('.vssl-stripe--embed')
    embedContainers.forEach((container) => {
      renderEmbedCode(container.innerHTML, container as HTMLElement)
    })
    // Hydrate vessel stripes after preview HTML is rendered
    if (window.hydrateVesselStripes) {
      window.hydrateVesselStripes()
    }
  }
})

onMounted(async () => {
  window.addEventListener('click', onWindowClick)
  window.addEventListener('pointerdown', onWindowPointerDown)
  window.addEventListener('keydown', onWindowKeydown)

  // If neither the access nor refresh token can be recovered mid-edit, save
  // whatever's unsaved and prompt the user to re-authenticate, instead of
  // just reloading into a logged-out page and losing their work.
  onSessionExpired(() => {
    state.persistUnsavedChanges()
    sessionExpiredModalOpen.value = true
  })

  try {
    const url = new URL(window.location.href)
    /**
     * For uva-sites (and other implementations we support in the future with server-side auth), the code-to-token exchange is happening on the server auth middleware. We will still support the code being sent in a query param and letting the frontend editor do the exchange, for when we want to implement Vessel on sites without server-side auth (e.g. Cville Free Clinic) -BM
     */
    const code = url.searchParams.get('code') || ''
    const autologin = url.searchParams.get('autologin') === 'true'
    const openComments = url.searchParams.get('comments') === 'true'
    // A copied version link; left in the URL while version history is open.
    const versionId = Number(url.searchParams.get('v')) || null
    await initAuth(code)
    authenticated.value = !!getToken()

    if (authenticated.value && openComments) {
      state.autoOpenComments.value = true
    }

    if (authenticated.value && versionId) {
      state.autoOpenVersionId.value = versionId
    }

    // Remove code parameter if authenticated.
    if (authenticated.value && code) {
      url.searchParams.delete('code')
    }

    // Remove autologin parameter if authenticated.
    if (authenticated.value && autologin) {
      url.searchParams.delete('autologin')
    }

    // Remove comments parameter if authenticated (it's been captured above).
    if (authenticated.value && openComments) {
      url.searchParams.delete('comments')
    }
    if (authenticated.value && (code || autologin || openComments)) {
      window.history.replaceState({}, '', url)
    }

    if (!authenticated.value && autologin) {
      // If not authenticated, attempt an auto-login.
      login()
    }
  } catch (e) {
    console.warn('Failed to authenticate: ', e)
  } finally {
    loaded.value = !authenticated.value
  }

  const [navigationBufferEl, toolbarBufferEl] = getBufferEls()
  if (navigationBufferEl) {
    navigationBufferEl.remove()
    document.body.prepend(navigationBufferEl)
  }
  if (toolbarBufferEl) {
    toolbarBufferEl.remove()
    document.body.append(toolbarBufferEl)
  }
})

onUnmounted(() => {
  window.removeEventListener('click', onWindowClick)
  window.removeEventListener('pointerdown', onWindowPointerDown)
  window.removeEventListener('keydown', onWindowKeydown)
})
</script>

<template>
  <template v-if="loaded && authenticated">
    <Teleport to="body">
      <AdminTools id="vssl-admin-area" @logout="onLogout" />
    </Teleport>
    <Teleport to="body">
      <AdminConfirmModal
        v-if="sessionExpiredModalOpen"
        heading="Your session has expired"
        body="Log in again to keep editing. Your unsaved changes will be kept."
        confirm-label="Log In"
        cancel-label="Dismiss"
        @confirm="reauthenticate"
        @cancel="sessionExpiredModalOpen = false"
      />
    </Teleport>
    <Teleport to="[vssl-document]">
      <div
        :class="[
          'vssl-page',
          { 'vssl-page--editor': !isPreviewing },
          { 'vssl-page--preview': isPreviewing }
        ]"
        :data-id="state.page.value.id"
        :data-type="state.page.value.type"
        :data-readonly="isReadonlyPage ? 'true' : undefined"
        :hidden="!state.isManagedPage.value"
      >
        <div
          v-if="state.pagePreviewHTML.value"
          class="vssl-stripes"
          :class="{
            'vssl-page-area--loading-preview': state.loadingPagePreview.value
          }"
          v-html="state.pagePreviewHTML.value"
        />
        <!-- Version history never shows the editable stripes. -->
        <div
          v-else-if="isVersionHistoryOpen"
          class="vssl-stripes vssl-page-area--loading-preview"
        />
        <StripeList
          v-else
          id="vssl-page-area"
          class="vssl-stripes"
          :class="{
            'vssl-page-area--loading-preview': state.loadingPagePreview.value
          }"
          :stripes="state.stripes.value"
        />
      </div>
    </Teleport>
  </template>

  <div v-show="authenticated" id="vssl-navigation-buffer" />
  <div
    v-show="authenticated && !isVersionHistoryOpen"
    id="vssl-toolbar-buffer"
  />
</template>

<style>
#vssl-navigation-buffer {
  height: 60px;
}

#vssl-toolbar-buffer {
  height: 64px;
}

.vssl-page-area--loading-preview {
  opacity: 0.333;
  pointer-events: none;
  transition: opacity 0.2s var(--vssl-ease-in-out-fast);
}

/* Version history: the page sits inset on a grue background, leaving room for
   the back bar above it and the sidebar beside it (see AdminVersionHistory.vue
   and toggleVersionHistoryInset above). */
/* The window doesn't scroll; the inset page does, so its scrollbar sits beside
   the preview rather than at the window's edge next to the sidebar. */
html:has(> body.vssl-version-history-open) {
  overflow: hidden;
}

body.vssl-version-history-open {
  background-color: var(--vssl-color-grue);
  box-sizing: border-box;
  height: 100vh;
  margin: 0;
  overflow: hidden;
  padding: 0 24px 24px;
}

body.vssl-version-history-open #vssl-navigation-buffer {
  height: calc(60px + 66px);
}

/* Fills what's left below the navigation buffer (60px nav + 66px back bar)
   and above the body's 24px bottom padding. */
.vssl-version-history-inset {
  background-color: var(--vssl-color-white);
  box-sizing: border-box;
  height: calc(100vh - 60px - 66px - 24px);
  max-height: none;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

@media (min-width: 900px) {
  body.vssl-version-history-open {
    padding-right: calc(380px + 24px);
  }
}
</style>
